import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import { useEventStatsStore, getEventTypeLabel, getCtaTargetLabel } from '../../stores/eventStatsStore'

const COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0',
  '#00BCD4', '#FF5722', '#607D8B', '#795548', '#3F51B5',
]

const SURVEY_TYPE_LABELS: Record<string, string> = {
  career_calling: '職涯呼召',
  outcome_expectation: '結果預期',
  bfi15: '性格特質（BFI-15）',
}

export default function EventStatsPanel() {
  const { eventStats, loading, error, fetchEventStats } = useEventStatsStore()
  const [selectedType, setSelectedType] = useState<string | null>(null)

  useEffect(() => {
    fetchEventStats()
  }, [fetchEventStats])

  if (loading && !eventStats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) return <Alert severity="error">{error}</Alert>

  if (!eventStats || Object.keys(eventStats.byEventType).length === 0) {
    return <Alert severity="info">目前尚無事件統計資料。</Alert>
  }

  // 総量表BarChart data
  const overviewData = Object.entries(eventStats.byEventType)
    .map(([key, v]) => ({ name: getEventTypeLabel(key), key, value: v.total }))
    .sort((a, b) => b.value - a.value)

  const selected = selectedType ? eventStats.byEventType[selectedType] : null

  return (
    <Box>
      {/* 概覧 BarChart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            事件總覽（依次數降序）
          </Typography>
          <Box sx={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <BarChart
                data={overviewData}
                layout="vertical"
                margin={{ top: 5, right: 40, left: 120, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v: number) => [`${v} 次`, '次數']} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {overviewData.map((_e, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      {/* 事件選択 + 詳細 */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          選擇事件類型查看明細
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {overviewData.map((item) => (
            <Chip
              key={item.key}
              label={`${item.name} (${item.value})`}
              onClick={() => setSelectedType(selectedType === item.key ? null : item.key)}
              color={selectedType === item.key ? 'primary' : 'default'}
              variant={selectedType === item.key ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Box>
      </Card>

      {selected && selectedType && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {getEventTypeLabel(selectedType)} — 明細
            </Typography>

            {/* byTarget */}
            {selected.byTarget && Object.keys(selected.byTarget).length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  目標項目分布
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>目標</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>次數</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>佔比</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(selected.byTarget)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 20)
                      .map(([key, count]) => (
                        <TableRow key={key} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">
                                {getCtaTargetLabel(key)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {key}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">{count}</TableCell>
                          <TableCell align="right">
                            {selected.total > 0
                              ? `${Math.round((count / selected.total) * 100)}%`
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* byType（survey 系） */}
            {selected.byType && Object.keys(selected.byType).length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  量表類型分布
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>量表類型</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>次數</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(selected.byType)
                      .sort((a, b) => b[1] - a[1])
                      .map(([key, count]) => (
                        <TableRow key={key} hover>
                          <TableCell>{SURVEY_TYPE_LABELS[key] ?? key}</TableCell>
                          <TableCell align="right">{count}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* terms（search） */}
            {selected.terms && Object.keys(selected.terms).length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  熱門搜尋詞（Top 20）
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>搜尋詞</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>次數</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(selected.terms)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 20)
                      .map(([term, count]) => (
                        <TableRow key={term} hover>
                          <TableCell>{term}</TableCell>
                          <TableCell align="right">{count}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* byTarget/byType/terms が全部なければ合計のみ表示 */}
            {!selected.byTarget && !selected.byType && !selected.terms && (
              <Typography variant="body2" color="text.secondary">
                合計：{selected.total} 次
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {eventStats.lastUpdatedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          最後更新：{eventStats.lastUpdatedAt.toLocaleString('zh-TW')}
        </Typography>
      )}
    </Box>
  )
}
